import { useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FileText, Shield, Settings, LogOut, X, Inbox,
  Banknote, Package,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useIsAdmin } from "@/hooks/useIsAdmin";

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  // 2026-02 round 12: pozycja „Administracja" widoczna wyłącznie dla adminów
  // (`ADMIN_EMAILS` po stronie backendu). Niezalogowany/non-admin dostaje
  // `false`; hook zwraca `null` podczas fetchowania — traktowane jak
  // non-admin (nie renderujemy, żeby uniknąć błysku itemu).
  const isAdmin = useIsAdmin();

  // Statyczna nawigacja workflow (Start → 1..5 → Administracja).
  // Nazwy są stałe, niezależne od statusów — postęp pokazywany jest wyłącznie na ekranie Start.
  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Start' },
    { path: '/ksef-config', icon: Shield, label: 'KSeF skonfigurowany', badge: '1' },
    { path: '/ksef-inbox', icon: Inbox, label: 'Import faktur', badge: '2' },
    { path: '/wyciagi', icon: Banknote, label: 'Wyciągi', badge: '3' },
    { path: '/invoices', icon: FileText, label: 'Faktury', badge: '4' },
    { path: '/paczki', icon: Package, label: 'Paczki', badge: '5' },
    // Admin-only — widoczne wyłącznie gdy `useIsAdmin() === true`.
    ...(isAdmin === true
      ? [{ path: '/settings', icon: Settings, label: 'Administracja', adminOnly: true }]
      : []),
  ];

  const handleNavClick = (path) => {
    navigate(path);
    onClose();
  };

  const handleLogout = async () => {
    onClose();
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={onClose}
          data-testid="sidebar-overlay"
        />
      )}
      
      <aside className={`sidebar ${isOpen ? 'open' : ''}`} data-testid="sidebar">
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold">PAX</h1>
            <p className="text-xs text-gray-400 mt-1">Panel operacyjny</p>
          </div>
          {/* Close button - mobile only */}
          <button 
            onClick={onClose}
            className="md:hidden touch-button text-gray-400 hover:text-white"
            data-testid="sidebar-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <nav className="py-4">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => handleNavClick(item.path)}
              className={`sidebar-link w-full ${location.pathname === item.path ? 'active' : ''}`}
              data-testid={`nav-${item.path.slice(1)}`}
            >
              {item.badge ? (
                <span
                  className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-white/10 text-[11px] font-semibold text-gray-200 shrink-0"
                  data-testid={`nav-badge-${item.badge}`}
                >
                  {item.badge}
                </span>
              ) : (
                <item.icon className="w-5 h-5" />
              )}
              <span className="flex-1 text-left truncate">{item.label}</span>
            </button>
          ))}
        </nav>
        
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <button 
            onClick={handleLogout}
            className="sidebar-link w-full justify-start text-red-300 hover:text-red-200 hover:bg-red-900/20"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            Wyloguj
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
